import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField as MuiTextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useNotify,
  usePermissions,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

const ConsentSection = ({ groupId }: { groupId: string }) => {
  const [pendingConsent, setPendingConsent] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()

  React.useEffect(() => {
    checkPendingConsent()
  }, [groupId])

  const checkPendingConsent = async () => {
    try {
      const response = await deadlineFetch('/api/group-members/my-consent-requests', {
        headers: authHeaders(),
      })

      if (response.ok) {
        const result = await response.json()
        const groupConsent = result.requests?.find(
          (req: any) => (typeof req.group === 'object' ? req.group.id : req.group) === groupId,
        )
        setPendingConsent(groupConsent)
      }
    } catch (error) {
      console.error('Error checking consent:', error)
    }
  }

  const handleConsent = async (consent: 'granted' | 'denied') => {
    if (!pendingConsent) return

    setLoading(true)
    try {
      const response = await deadlineFetch('/api/group-members/update-consent', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          membershipId: pendingConsent.id,
          consent,
        }),
      })

      const result = await response.json()

      if (response.ok) {
        notify(result.message, { type: 'success' })
        setPendingConsent(null)
        refresh()
      } else {
        notify(result.error || translate('resources.groups.notifications.consent_update_failed'), {
          type: 'error',
        })
      }
    } catch (error) {
      notify('resources.groups.notifications.consent_update_failed', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!pendingConsent) return null

  return (
    <Card sx={{ mt: 2, mb: 2, backgroundColor: '#fff3cd', borderColor: '#ffeaa7' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" color="warning.main">
              {translate('resources.groups.ui.invitation_pending')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.groups.ui.invitation_pending_desc')}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={() => handleConsent('granted')}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {translate('resources.groups.ui.accept')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => handleConsent('denied')}
              disabled={loading}
            >
              {translate('resources.groups.ui.decline')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

const GroupMemberManager = ({ groupId }: { groupId: string }) => {
  console.log('🔍 GroupMemberManager received groupId:', groupId, typeof groupId)

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [members, setMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const { permissions } = usePermissions()
  const translate = useTranslate()

  React.useEffect(() => {
    loadMembers()
  }, [groupId])

  const loadMembers = async () => {
    try {
      const { data } = await dataProvider.getList('group-members', {
        filter: { group: groupId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
      })
      setMembers(data)
    } catch (error) {
      notify('resources.groups.notifications.members_load_failed', { type: 'error' })
    }
  }

  const handleInvite = async () => {
    if (!email.trim()) {
      notify('resources.groups.notifications.email_required', { type: 'error' })
      return
    }

    if (!groupId) {
      notify('resources.groups.notifications.group_id_missing', { type: 'error' })
      return
    }

    setLoading(true)
    try {
      console.log('🔍 Sending invitation with:', { groupId, email: email.trim() })

      const response = await deadlineFetch('/api/group-members/invite', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          groupId,
          email: email.trim(),
        }),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        notify(result.message, { type: 'success' })
        setEmail('')
        setOpen(false)
        loadMembers()
        refresh()
      } else {
        notify(result.error || translate('resources.groups.notifications.invite_failed'), {
          type: 'error',
        })
      }
    } catch (error) {
      notify('resources.groups.notifications.invite_failed', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm(translate('resources.groups.ui.remove_member_confirm'))) {
      return
    }

    try {
      await dataProvider.delete('group-members', { id: memberId })
      notify('resources.groups.notifications.member_removed', { type: 'success' })
      loadMembers()
      refresh()
    } catch (error) {
      notify('resources.groups.notifications.member_remove_failed', { type: 'error' })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'invited':
        return 'warning'
      case 'declined':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {translate('resources.groups.ui.group_members')}{' '}
            {permissions?.role === 'siteadmin' && translate('resources.groups.ui.read_only')}
          </Typography>
          {/* Only vetadmin can invite members */}
          {permissions?.role === 'vetadmin' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              {translate('resources.groups.ui.invite_member')}
            </Button>
          )}
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('resources.groups.ui.col_email')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_user')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_status')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_invited_at')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member: any) => (
              <TableRow key={member.id}>
                <TableCell>{member.email}</TableCell>
                <TableCell>{member.user ? member.user.name || member.user.email : '-'}</TableCell>
                <TableCell>
                  <Chip label={member.status} color={getStatusColor(member.status)} size="small" />
                </TableCell>
                <TableCell>{new Date(member.invitedAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {/* Only vetadmin can remove members */}
                  {permissions?.role === 'vetadmin' && (
                    <IconButton
                      onClick={() => handleRemoveMember(member.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{translate('resources.groups.ui.invite_new_member')}</DialogTitle>
          <DialogContent>
            <MuiTextField
              autoFocus
              margin="dense"
              label={translate('resources.groups.ui.email_address')}
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>
              {translate('resources.groups.ui.cancel')}
            </Button>
            <Button onClick={handleInvite} variant="contained" disabled={loading}>
              {loading
                ? translate('resources.groups.ui.sending')
                : translate('resources.groups.ui.send_invitation')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}

const GroupShowInner = () => {
  const { record, isLoading } = useShowContext()
  const { permissions } = usePermissions()
  const translate = useTranslate()

  console.log('🔍 GroupShowInner - record:', record)
  console.log('🔍 GroupShowInner - record.id:', record?.id, typeof record?.id)

  if (isLoading || !record) return <div>{translate('resources.groups.ui.loading')}</div>

  return (
    <div>
      <SimpleShowLayout>
        <TextField source="name" />
        <TextField source="description" />
        <FunctionField
          label={translate('resources.groups.ui.administrators')}
          render={(record: any) => {
            if (!record.admins || !Array.isArray(record.admins)) {
              return <span>-</span>
            }
            return (
              <div>
                {record.admins.map((admin: any, index: number) => (
                  <Chip
                    key={index}
                    label={
                      admin.name ||
                      admin.email ||
                      translate('pos.common.user_fallback', { id: admin.id || admin })
                    }
                    size="small"
                    style={{ marginRight: 4, marginBottom: 4 }}
                  />
                ))}
              </div>
            )
          }}
        />
        <DateField source="createdAt" showTime />
        <DateField source="updatedAt" showTime />
      </SimpleShowLayout>

      {/* Consent Management Section for regular vets */}
      {permissions?.role === 'vet' && <ConsentSection groupId={record.id} />}

      <GroupMemberManager groupId={record.id} />
    </div>
  )
}

const ShowActions = () => {
  const { permissions } = usePermissions()

  // Only vetadmin can edit groups (vet and siteadmin are read-only)
  if (permissions?.role !== 'vetadmin') {
    return <TopToolbar />
  }

  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  )
}

const GroupShow = () => (
  <Show actions={<ShowActions />}>
    <GroupShowInner />
  </Show>
)

export default GroupShow
